import { memo, useCallback, useMemo, useState } from 'react'

import { Refresh } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Foldout } from '@shared/components/Foldout'
import { AnimatedCounterButton } from '@shared/components/MaterialUIEnhancements/IconButton'
import { SearchBar } from '@shared/components/SearchBar'
import { ActionStrip } from '@shared/layouts'
import { SCROLL_BUTTON_ID } from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Monitoring'
import { Overview as Alerts } from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Monitoring/Alerts'
import { FilterPanel, Overview as Logs } from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Monitoring/Logs'
import { avcTaskMonitoringRoute } from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks/AvcTask'
import { avcTaskQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const queryClient = useQueryClient()

  const [filterOpen, setFilterOpen] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)

  const navigate = useNavigate({ from: avcTaskMonitoringRoute.fullPath })
  const { avcTaskId: taskId } = avcTaskMonitoringRoute.useParams()
  const queryStrings = avcTaskMonitoringRoute.useSearch()

  const filterParamsNum = useMemo(
    () => Object.keys(queryStrings).filter((param) => param !== 'query').length,
    [queryStrings],
  )

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries(avcTaskQueryKeys.logs({ taskId, ...queryStrings, query: queryStrings.query }))
    queryClient.invalidateQueries(avcTaskQueryKeys.detail({ taskId }))
  }, [queryStrings, queryClient, taskId])

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <ActionStrip>
          <Typography variant="heading-md">{t('domain:Workspace.AvcTask.alert', { count: 2 })}</Typography>
        </ActionStrip>
        <Box sx={{ height: 200, flexShrink: 0 }}>
          <Alerts />
        </Box>

        <ActionStrip sx={{ mt: 2 }}>
          <Typography variant="heading-md">{t('domain:Workspace.AvcTask.log', { count: 2 })}</Typography>
        </ActionStrip>

        <ActionStrip>
          <SearchBar
            placeholder={t('domain:Workspace.AvcTask.searchLogs')}
            onSearch={({ search }) => navigate({ search: { ...queryStrings, query: search } })}
            defaultValue={queryStrings.query}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, ml: 'auto' }}>
            <Button
              sx={{ textTransform: 'none' }}
              disabled={autoRefresh}
              onClick={() => document.getElementById(SCROLL_BUTTON_ID)?.click()}
            >
              {autoRefresh ? `(${t('autoRefresh')})` : t('domain:Workspace.AvcTask.enableAutoRefresh')}
            </Button>
            <Button onClick={() => setFilterOpen(true)}>
              {t('domain:Workspace.AvcTask.applyFilters')} ({filterParamsNum})
            </Button>
            <AnimatedCounterButton action={handleRefresh}>{(props) => <Refresh {...props} />}</AnimatedCounterButton>
          </Box>
        </ActionStrip>

        <Logs toggleAutoRefresh={setAutoRefresh} />
      </Box>
      <Foldout open={filterOpen} onClose={() => setFilterOpen(false)} placement="end" hideBackdrop={false}>
        <FilterPanel />
      </Foldout>
    </>
  )
}

export const Overview = memo(OverviewRaw)
